import {
    AccountOrder,
    AccountOrderItem,
    AccountPaymentItem,
    AccountPaymentSelection,
    AccountPriceBreakdown,
    OpenTabAccount,
    OpenTabAccountSummary,
} from '@/features/openTabs/types'
import currencyJs from 'currency.js'

export const roundMoney = (amount: number): number => currencyJs(amount).value

const resolveItemQuantity = (item: AccountOrderItem, quantity: number): number =>
    Number.isSafeInteger(item.quantity) &&
    item.quantity > 0 &&
    Number.isSafeInteger(quantity) &&
    quantity > 0
        ? Math.min(quantity, item.quantity)
        : 0

export const getAccountItemRemainder = (
    item: AccountOrderItem,
    paidQuantity: number
): AccountOrderItem => {
    const quantity =
        resolveItemQuantity(item, item.quantity) - resolveItemQuantity(item, paidQuantity)

    return {
        ...item,
        quantity,
        totalAmount: getAccountItemTotal(item, quantity),
    }
}

export const getAccountItemTotal = (
    item: AccountOrderItem,
    quantity: number = item.quantity
): number => currencyJs(item.unitPrice).multiply(resolveItemQuantity(item, quantity)).value

export const getAccountItemPaymentTotal = (item: AccountOrderItem, paidQuantity: number): number =>
    getAccountItemTotal(item, paidQuantity)

export const getAccountAmountDue = (account: OpenTabAccountSummary): number =>
    account.unsettledAmount

export const getAccountDisplayAmount = (account: OpenTabAccountSummary): number =>
    account.status === 'closed' ? account.totalAmount : getAccountAmountDue(account)

export const getAccountItemCount = (
    orders: readonly { items: readonly { quantity: number }[] }[]
): number =>
    orders.reduce(
        (total, order) =>
            total + order.items.reduce((orderTotal, item) => orderTotal + item.quantity, 0),
        0
    )

export const getAccountPriceBreakdown = (
    account?: OpenTabAccount,
    selection?: AccountPaymentSelection
): AccountPriceBreakdown => {
    if (!account) return { productsAmount: 0, containerAmount: 0 }

    const containerAmount = account.orders
        .flatMap((order) => order.items)
        .reduce((total, item) => {
            const quantity = resolveItemQuantity(
                item,
                selection ? (selection[item.id] ?? 0) : item.quantity
            )

            return total.add(currencyJs(item.unitContainerPrice).multiply(quantity))
        }, currencyJs(0)).value
    const totalAmount = selection
        ? getSelectedPaymentTotal(account, selection)
        : getAccountDisplayAmount(account)

    return {
        productsAmount: currencyJs(totalAmount).subtract(containerAmount).value,
        containerAmount,
    }
}

export const getAccountsTotal = (accounts: readonly OpenTabAccountSummary[]): number =>
    accounts.reduce((total, account) => total.add(getAccountDisplayAmount(account)), currencyJs(0))
        .value

export const isValidAccountSelection = (
    account: OpenTabAccount | undefined,
    selection: AccountPaymentSelection
): boolean => {
    if (!account || account.status !== 'open') return false

    const items = account.orders.flatMap((order) => order.items)
    const quantities = new Map(items.map((item) => [item.id, item.quantity]))
    const selectedItems = Object.entries(selection)

    if (
        !selectedItems.length ||
        quantities.size !== items.length ||
        items.some((item) => !item.id.trim())
    ) {
        return false
    }

    return selectedItems.every(([id, quantity]) => {
        const availableQuantity = quantities.get(id)

        return (
            Number.isSafeInteger(quantity) &&
            quantity > 0 &&
            availableQuantity !== undefined &&
            Number.isSafeInteger(availableQuantity) &&
            quantity <= availableQuantity
        )
    })
}

export const isFullAccountSelection = (
    account: OpenTabAccount | undefined,
    selection: AccountPaymentSelection
): boolean => {
    if (!account || !isValidAccountSelection(account, selection)) return false

    return account.orders.every((order) =>
        order.items.every((item) => selection[item.id] === item.quantity)
    )
}

export const getAccountPaymentItems = (
    account: OpenTabAccount | undefined,
    selection: AccountPaymentSelection,
    selected = true
): AccountPaymentItem[] => {
    if (!account) return []

    return account.orders.flatMap((order) =>
        order.items.flatMap((item) => {
            const selectedQuantity = resolveItemQuantity(item, selection[item.id] ?? 0)
            const quantity = selected
                ? selectedQuantity
                : resolveItemQuantity(item, item.quantity) - selectedQuantity

            return quantity > 0 ? [{ item, orderId: order.id, quantity }] : []
        })
    )
}

export const getSelectedPaymentTotal = (
    account: OpenTabAccount | undefined,
    selection: AccountPaymentSelection
): number =>
    Object.values(getAccountPaymentItemTotals(account, selection)).reduce(
        (total, amount) => total.add(amount),
        currencyJs(0)
    ).value

const getOrderPaymentAllocations = (
    order: AccountOrder,
    selection: AccountPaymentSelection
): { itemId: string; amount: number }[] =>
    order.items.flatMap((item) => {
        const selectedQuantity = resolveItemQuantity(item, selection[item.id] ?? 0)

        return selectedQuantity > 0
            ? [{ itemId: item.id, amount: getAccountItemPaymentTotal(item, selectedQuantity) }]
            : []
    })

export const getAccountPaymentItemTotals = (
    account: OpenTabAccount | undefined,
    selection: AccountPaymentSelection
): Record<string, number> => {
    if (!account) return {}

    return account.orders.reduce<Record<string, number>>((totals, order) => {
        getOrderPaymentAllocations(order, selection).forEach(({ itemId, amount }) => {
            totals[itemId] = amount
        })

        return totals
    }, {})
}

export const getOrderPaymentTotal = (
    order: AccountOrder,
    selection: AccountPaymentSelection
): number =>
    getOrderPaymentAllocations(order, selection).reduce(
        (total, allocation) => total.add(allocation.amount),
        currencyJs(0)
    ).value

export const getAccountPaymentTotal = (
    account: OpenTabAccount | undefined,
    selection: AccountPaymentSelection
): number => {
    if (!account || !isValidAccountSelection(account, selection)) return 0

    const total = getSelectedPaymentTotal(account, selection)

    if (account.currency === 'EUR') return total

    const roundedTotal = currencyJs(Math.abs(total), { precision: 0 }).value

    return total < 0 && roundedTotal > 0 ? -roundedTotal : roundedTotal
}
